import { ECountry } from 'common/const';
import { IOrganization } from 'common/types/organization.types';

/* Дробный режим только для ME; RS и неизвестная страна — целые (US-023, безопасный дефолт). */
export const isFractionalAmountEnabled = (organization?: IOrganization | null): boolean => organization?.country === ECountry.ME;

/* Маска дробного ввода: цифры, один разделитель «.»/«,», максимум 2 знака после.
 *  Значение, не влезающее в маску, не принимается — состояние остаётся прежним:
 *  так «лишние нажатия игнорируются в момент ввода» (критерий 4 US-023), включая вставку. */
const FRACTIONAL_AMOUNT_REGEXP = /^\d*([.,]\d{0,2})?$/;

export const sanitizeFractionalAmountInput = (raw: string, prev: string): string =>
  FRACTIONAL_AMOUNT_REGEXP.test(raw) ? raw : prev;

/* Парсер строки ввода: запятая эквивалентна точке (критерий 4 US-023);
 *  незавершённый ввод «1.» / «1,» — валидная целая 1 (edge case US-023,
 *  Number('1.') === 1 нативно); пустая строка → 0 (валидация отсекает, как сегодня).
 *  Для digit-only строк RS результат тождествен Number() — И1. */
export const parseAmountInput = (value: string): number => Number(value.replace(',', '.'));

/* Округление перед отправкой — страновое: RS — сегодняшний Math.floor
 *  (бывший useService «round summ for apple pay»), ME — сумма как есть:
 *  гость платит ровно столько, сколько ввёл (критерий 1 US-023, C-021). */
export const prepareAmountForSubmit = (amount: number, organization?: IOrganization | null): number =>
  isFractionalAmountEnabled(organization) ? amount : Math.floor(amount);

/* Подпись суммы по стране (US-034, R-5).
 *  ME: число и «€» через пробел; целые без дробной части, дробные — два знака с точкой
 *  («2 €», «0.50 €», «2.50 €»), разделители от языка не зависят (как на терминале, US-032);
 *  значение точнее цента показывается округлённым до цента.
 *  RS: сегодняшняя подпись пресета «585 din» (И1). */
const toCents = (value: number): number => {
  // Сдвиг через экспоненту, а не value * 100: у произведения бывает погрешность float
  // (4.015 * 100 = 401.49999…, Math.round дал бы 401), у Number('4.015e2') — ровно 401.5.
  // Полуцент вверх, как Money.mul на бэке (F-23). Экспоненциальная запись value (1e-7)
  // сдвигом не читается — для неё прежний путь.
  const shifted = Number(`${value}e2`);

  return Math.round(Number.isFinite(shifted) ? shifted : value * 100);
};

export const formatMoney = (value: number, country: ECountry): string => {
  if (country !== ECountry.ME) {
    return `${value} din`;
  }

  const cents = toCents(value);
  const amount = cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);

  return `${amount} €`;
};
